import { useCallback, useEffect, useState } from "react";
import { GitCompare, Send, Loader2, Mail } from "lucide-react";
import { AdminEligibility, currentUser } from "@/lib/api";
import { toast } from "sonner";

const MONEY = new Set(["verified_income", "approved_amount", "total_emi"]);
const fmtV = (v, field) => {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.length ? `${v.length} item(s)` : "None";
  if (typeof v === "boolean") return v ? "Available" : "Not available";
  if (typeof v === "number") return MONEY.has(field) ? `₹${v.toLocaleString("en-IN")}` : field === "foir" ? `${v}%` : String(v);
  return String(v);
};
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

/** READ-ONLY Version Compare + immutable Sent-Letter History. */
export const LetterVersionTools = ({ check, versions, onSent }) => {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [cmp, setCmp] = useState(null);
  const [sends, setSends] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (versions?.length >= 2 && !a && !b) { setB(versions[0].id); setA(versions[1].id); }
  }, [versions, a, b]);

  const loadSends = useCallback(async () => { try { setSends((await AdminEligibility.letterSends(check.id)).sends); } catch { setSends([]); } }, [check.id]);
  useEffect(() => { loadSends(); }, [loadSends]);

  const compare = async () => {
    if (!a || !b || a === b) { setErr("Select two different versions."); return; }
    setBusy("cmp"); setErr("");
    try { setCmp(await AdminEligibility.letterCompare(check.id, a, b)); } catch (e) { setErr(e?.response?.data?.detail || "Compare failed"); } finally { setBusy(""); }
  };
  const sendVersion = async (v) => {
    if (!window.confirm(`Send v${v.version_no} · ${v.reference_no} to ${check.email || "customer"}?\nThe exact PDF of this version will be attached.`)) return;
    setBusy(`send-${v.id}`); setErr("");
    try {
      const r = await AdminEligibility.sendLetter(check.id, { version_id: v.id });
      toast.success(`Letter v${r.version_no ?? v.version_no} sent`, { description: `${r.reference_no} → ${check.email || "customer"}` });
      onSent?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "SUPERSEDE_CONFIRMATION_REQUIRED") {
        setErr("Send cancelled — the newer version was not sent. Nothing was recorded.");
        toast("Send cancelled", { description: "The newer version was not sent. Nothing was recorded." });
      } else {
        const msg = d?.message_en || (typeof d === "string" ? d : "Send failed — see Sent Letter History");
        setErr(msg);
        toast.error(`Letter v${v.version_no} not sent`, { description: msg });
      }
    } finally { setBusy(""); await loadSends(); }
  };

  const me = currentUser();
  const sendTest = async (v) => {
    setBusy(`test-${v.id}`); setErr("");
    try {
      const r = await AdminEligibility.sendTestToMe(check.id, v.id);
      toast.success(`Test Letter V${r.version_no} sent to your verified email.`, { description: r.recipient });
    } catch (e) {
      const d = e?.response?.data?.detail; toast.error("Test email failed. Review delivery configuration.", { description: d?.message_en || (typeof d === "string" ? d : "") });
    } finally { setBusy(""); await loadSends(); }
  };
  const [fixEmail, setFixEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailReason, setEmailReason] = useState("");
  const correctEmail = async () => {
    try {
      await AdminEligibility.correctEmail(check.id, { new_email: newEmail, reason: emailReason });
      toast.success("Customer email corrected (audited)", { description: newEmail }); setFixEmail(false); onSent?.();
    } catch (e) { const d = e?.response?.data?.detail; toast.error("Email not changed", { description: typeof d === "string" ? d : d?.[0]?.msg || "" }); }
  };
  const vLabel = (id) => { const v = versions?.find((x) => x.id === id); return v ? `v${v.version_no} · ${v.reference_no}` : "—"; };
  const sel = "rounded-lg bg-[#04102B] border border-white/20 px-2 py-1.5 text-xs text-white min-w-0 max-w-full";

  return (
    <div data-testid="letter-version-tools" className="mt-3 space-y-3">
      {versions?.length >= 2 && (
        <div className="rounded-lg border border-white/15 p-2.5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] inline-flex items-center gap-1.5"><GitCompare size={12} /> Compare Versions (read-only)</div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <label className="text-white/70">A <select data-testid="compare-a" value={a} onChange={(e) => setA(e.target.value)} className={sel}>{versions.map((v) => <option key={v.id} value={v.id}>v{v.version_no} · {v.reference_no}</option>)}</select></label>
            <label className="text-white/70">B <select data-testid="compare-b" value={b} onChange={(e) => setB(e.target.value)} className={sel}>{versions.map((v) => <option key={v.id} value={v.id}>v{v.version_no} · {v.reference_no}</option>)}</select></label>
            <button data-testid="compare-run" onClick={compare} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 text-white/85 hover:border-[#D4AF37] disabled:opacity-50 min-h-[36px]">
              {busy === "cmp" ? <Loader2 size={12} className="animate-spin" /> : <GitCompare size={12} />} Compare
            </button>
          </div>
          {cmp && (
            <div data-testid="compare-result" className="mt-3">
              {cmp.previously_sent && (
                <div data-testid="compare-previously-sent" className="mb-2 text-[11px] text-amber-200">
                  PREVIOUSLY SENT VERSION: v{cmp.previously_sent.version_no} · {cmp.previously_sent.reference_no} · {when(cmp.previously_sent.sent_at)} → {cmp.previously_sent.recipient}
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-2 text-[11px] text-white/75">
                {[cmp.a, cmp.b].map((m, i) => (
                  <div key={m.version_id} className="rounded-md border border-white/10 p-2 wrap-anywhere">
                    <div className="text-[#F0D788] font-semibold">{i === 0 ? "A" : "B"} · v{m.version_no} · {m.reference_no}</div>
                    <div>{m.template_version} · {m.engine_version} · sha {String(m.pdf_sha256).slice(0, 10)}…</div>
                    <div>Generated {when(m.generated_at)} by {m.generated_by}{m.reviewed_by ? ` · Reviewed by ${m.reviewed_by}` : ""}</div>
                    {m.regeneration_reason && <div>Reason: {m.regeneration_reason}</div>}
                    {m.override_reasons?.map((o) => <div key={o.audit_ref} className="text-white/60">↳ {o.field.replace(/_/g, " ")}: {o.reason} · {o.by} · audit {String(o.audit_ref).slice(0, 8)}</div>)}
                  </div>
                ))}
              </div>
              <div data-testid="compare-cards" className="mt-2 grid gap-2">
                {cmp.rows.map((r) => (
                  <div key={r.field} data-testid={`compare-row-${r.field}`} className={`rounded-md border p-2 text-[11px] ${r.changed ? "border-[#D4AF37]/50 bg-[#D4AF37]/10" : "border-white/10"}`}>
                    <div className="flex flex-wrap items-center gap-2 text-white/90 font-medium">
                      {r.label}{r.changed && <span className="px-1.5 rounded-full text-[8px] font-bold uppercase border border-[#D4AF37]/60 text-[#F0D788]">Changed</span>}
                    </div>
                    <div className="mt-1.5 grid grid-cols-2 gap-x-3">
                      {[["A", r.a], ["B", r.b]].map(([k, v]) => (
                        <div key={k} className="min-w-0 wrap-anywhere">
                          <div className="text-[9px] uppercase tracking-wider text-white/60">{k} · v{k === "A" ? cmp.a.version_no : cmp.b.version_no}</div>
                          <div className="text-white/65">System: {fmtV(v.system, r.field)}</div>
                          <div className="text-amber-200">Override: {fmtV(v.override, r.field)}</div>
                          <div className={`font-semibold ${k === "B" && r.changed ? "text-[#F0D788]" : "text-white"}`}>Effective: {fmtV(v.effective, r.field)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {versions?.length > 0 && (
        <div className="rounded-lg border border-white/15 p-2.5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Send · Test Recipient: <span className="text-white/85 normal-case tracking-normal">{me?.email || "—"}</span> · Customer: <span className="text-white/85 normal-case tracking-normal">{check.email || "—"}</span></div>
          <div className="mt-2 flex flex-wrap gap-2">
            {versions.slice(0, 3).map((v) => (
              <div key={v.id} className="inline-flex flex-wrap gap-1">
                <button data-testid={`send-test-${v.version_no}`} onClick={() => sendTest(v)} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-400/40 text-amber-100 text-[11px] hover:bg-amber-500/10 disabled:opacity-50 min-h-[36px]">
                  {busy === `test-${v.id}` ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Send test v{v.version_no} to me
                </button>
                <button data-testid={`send-version-${v.version_no}`} onClick={() => sendVersion(v)} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-400/40 text-emerald-100 text-[11px] hover:bg-emerald-500/10 disabled:opacity-50 min-h-[36px]">
                  {busy === `send-${v.id}` ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send v{v.version_no} to customer
                </button>
              </div>
            ))}
          </div>
          <button data-testid="correct-email-toggle" onClick={() => setFixEmail((f) => !f)} className="mt-2 text-[11px] text-white/70 underline underline-offset-2">Correct customer email</button>
          {fixEmail && (
            <div data-testid="correct-email-form" className="mt-2 grid sm:grid-cols-2 gap-2 text-xs">
              <input data-testid="correct-email-value" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="new.address@example.com" className="rounded-lg bg-[#04102B] border border-white/20 px-3 py-2 text-white" />
              <input data-testid="correct-email-reason" value={emailReason} onChange={(e) => setEmailReason(e.target.value)} placeholder="Reason (mandatory, ≥10 chars)" className="rounded-lg bg-[#04102B] border border-white/20 px-3 py-2 text-white" />
              <button data-testid="correct-email-save" onClick={correctEmail} disabled={emailReason.trim().length < 10 || !newEmail} className="sm:col-span-2 justify-self-start px-4 py-2 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/60 text-[#F0D788] disabled:opacity-50 min-h-[36px]">Save corrected email (audited)</button>
            </div>
          )}
        </div>
      )}
      {err && <div data-testid="version-tools-error" className="text-xs text-rose-300">{String(err)}</div>}

      <div data-testid="sent-letter-history" className="rounded-lg border border-white/15 p-2.5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] inline-flex items-center gap-1.5"><Mail size={12} /> Sent Letter History</div>
        {!sends?.length ? <div className="mt-1 text-[11px] text-white/60">No letter has been sent yet.</div> : sends.map((s) => (
          <div key={s.id} data-testid={`sent-event-${s.id}`} className="mt-2 text-[11px] border-l-2 pl-2 wrap-anywhere" style={{ borderColor: s.status === "sent" ? "rgba(52,211,153,.7)" : "rgba(251,113,133,.7)" }}>
            <div className="text-white/90 font-semibold">{s.event_type === "TEST_SEND" && <span className="mr-1 px-1.5 rounded-full text-[8px] font-bold uppercase bg-amber-500/20 text-amber-200">Test send</span>}v{s.version_no ?? "—"} · {s.reference_no} <span className={`ml-1 px-1.5 rounded-full text-[8px] font-bold uppercase ${s.status === "sent" ? "bg-emerald-500/20 text-emerald-200" : "bg-rose-500/20 text-rose-200"}`}>{s.status}</span></div>
            <div className="text-white/70">{when(s.sent_at)} · Sent by {s.sent_by_name} · {s.channel} → {s.recipient}</div>
            {s.delivery_status && (
              <div data-testid={`delivery-status-${s.id}`} className="text-white/80">
                Delivery: <span className={`px-1.5 rounded-full text-[8px] font-bold uppercase ${["bounced", "complained", "failed"].includes(s.delivery_status) ? "bg-rose-500/20 text-rose-200" : ["delivered", "opened", "clicked"].includes(s.delivery_status) ? "bg-emerald-500/20 text-emerald-200" : "bg-white/10 text-white/80"}`}>{s.delivery_status}</span>
                {s.delivery_updated_at ? ` · ${when(s.delivery_updated_at)}` : ""}{s.delivery_events?.length ? ` · ${s.delivery_events.length} event(s)` : ""}
              </div>
            )}
            <div className="text-white/60">Provider {s.provider}{s.provider_message_id ? ` · msg ${s.provider_message_id}` : ""} · sha {String(s.pdf_sha256 || "").slice(0, 10)}…{s.status !== "sent" && s.provider_reason ? ` · ${s.provider_reason}` : ""}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
